import React, { useEffect, useState, useRef } from "react";
import {
  PatternlibOverlayContainer,
  PatternlibIcon,
  PatternlibTextlink,
} from "@liebherr/patternlib-react";
import { useTranslation } from "react-i18next";
import styles from "./InfoButton.module.css";
import PropTypes from "prop-types";
import { validateInfoKeys } from "../../helpers/helpers";
import Separator from "../separator/Separator";
import series260_manual_de from "../../pdf/260_bar_Produktschluessel.pdf";
import series260_manual_en from "../../pdf/260_bar_Product_key.pdf";
import series380_manual_de from "../../pdf/380_bar_Produktschluessel.pdf";
import series380_manual_en from "../../pdf/380_bar_Product_key.pdf";
import iso6022_manual_de from "../../pdf/ISO6022_Produktschluessel.pdf";
import iso6022_manual_en from "../../pdf/ISO6022_Product_key.pdf";
import useMutationObserver from "../../hooks/useMutationObserver";

const InfoButton = ({
  title,
  abbreviation,
  path,
  cadenasInfo,
  series,
  showInfoText,
}) => {
  const overlayRef = useRef();
  const [show, setShow] = useState(false);
  const { t, i18n } = useTranslation();

  // use mutation observer to get current screen size
  const options = {
    config: { attributes: true, childList: true, subtree: true },
  };
  const target = document.documentElement;
  const attributeName = "breakpoint";
  const screen = useMutationObserver(options, target, attributeName);

  // handle click on button to open modal
  const onButtonClick = () => {
    setShow(true);
  };

  // handle click on close icon to close modal
  const onClose = () => {
    setShow(false);
  };

  // attach event listener to backdrop
  useEffect(() => {
    if (show) {
      const ref = overlayRef.current;
      const element = ref.shadowRoot.querySelector(".backdrop");
      const content = ref.shadowRoot.querySelector(".content");
      content.style.zIndex = "9999";

      if (element !== null) {
        element.addEventListener("click", onClose, false);

        return () => {
          element.removeEventListener("click", onClose, true);
        };
      }
    }
  }, [show]);

  const getManual = () => {
    switch (series) {
      case "S26":
        return i18n.language === "de-DE" || i18n.language === "de"
          ? series260_manual_de
          : series260_manual_en;
      case "S38":
        return i18n.language === "de-DE" || i18n.language === "de"
          ? series380_manual_de
          : series380_manual_en;
      case "ISO":
        return i18n.language === "de-DE" || i18n.language === "de"
          ? iso6022_manual_de
          : iso6022_manual_en;
      default:
        return null;
    }
  };

  if (validateInfoKeys(path, abbreviation)) {
    return (
      <PatternlibOverlayContainer
        target="center"
        show={show}
        disable-click-handlers
        ref={overlayRef}
      >
        <button
          onClick={onButtonClick}
          className={styles.button}
          slot="element"
        >
          <PatternlibIcon icon-name="info" size="24px" slot="icon" />
        </button>
        <div slot="content" className={styles.modalContent}>
          {screen !== "isMobile" && (
            <PatternlibIcon icon-name="info-circle" size="64px" />
          )}
          <div className={styles.modalContentWrapper}>
            <div className={styles.modalHeader}>
              <h2>{title}</h2>
              <div className={styles.closeIcon}>
                <PatternlibIcon
                  icon-name="x"
                  onClick={onClose}
                ></PatternlibIcon>
              </div>
            </div>
            {showInfoText && (
              <p className={styles.infoText}>
                {t(`infotexts.${path + "." + abbreviation}.text`)}
              </p>
            )}
            {/* diaplay link */}
            {abbreviation === "DE" && (
              <>
                <span>
                  {t(`infotexts.${path + "." + abbreviation}.linkDesc`)}
                </span>
                <PatternlibTextlink
                  label={t(`infotexts.${path + "." + abbreviation}.linkTitle`)}
                  uppercase="false"
                  icon-position="left"
                  href={t(`infotexts.${path + "." + abbreviation}.url`)}
                ></PatternlibTextlink>
                <Separator space="xxxxxs"></Separator>
              </>
            )}
            {/* diaplay link to download manual for product key */}
            {abbreviation === "key" && (
              <>
                <PatternlibTextlink
                  label={t(`infotexts.general.key.linkTitle`)}
                  uppercase="false"
                  icon-position="left"
                  href={getManual()}
                ></PatternlibTextlink>
                <Separator space="xxxxxs"></Separator>
              </>
            )}
            {/* diaplay image */}
            {cadenasInfo && cadenasInfo["current"] && (
              <img
                src={
                  `https://webapi.partcommunity.com/FileService/CatalogImage?PreviewPath=liebherr_components/${path}/` +
                  cadenasInfo["current"]
                }
                alt="img"
              ></img>
            )}
          </div>
        </div>
      </PatternlibOverlayContainer>
    );
  } else {
    return <></>;
  }
};

InfoButton.propTypes = {
  title: PropTypes.string,
  abbreviation: PropTypes.string,
  path: PropTypes.string,
  series: PropTypes.string,
  showInfoText: PropTypes.bool,
};

InfoButton.defaultProps = {
  showInfoText: true,
};

export default InfoButton;
